import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { inviteProToJob, requestPro } from '../../api/pros'
import ProPublicCard from '../../pros/components/ProPublicCard'
import type { ProCardViewModel } from '../../pros/components/types'

type PixePros = {
  is_enabled: boolean
  title?: string
  bio?: string
  service_description?: string
  pro_mode?: 'REMOTE' | 'LOCAL' | 'BOTH'
  hourly_rate_cents?: number | null
  base_rate_cents?: number | null
  currency?: string
  available_now?: boolean
  skills?: string[] | string | null
  average_rating?: number
  rating_count?: number
  completed_jobs_count?: number
  total_earned_pixe?: string | number | null
  pros_to_go_enabled?: boolean
  company_role_title?: string
  company?: {
    id: number
    slug: string
    title: string
    avatar_url?: string
  } | null
}

function normalizeSkills(input: PixePros['skills']): string[] {
  if (Array.isArray(input)) {
    return input.map((s) => String(s || '').trim()).filter(Boolean)
  }
  if (typeof input === 'string') {
    return input.split(',').map((s) => s.trim()).filter(Boolean)
  }
  return []
}

function parseApiError(
  error: any,
  fallback: string
): { message: string; code?: string; status?: number; url?: string; method?: string; dataPreview?: string } {
  const status = typeof error?.response?.status === 'number' ? error.response.status : undefined
  const data = error?.response?.data
  const url = typeof error?.config?.url === 'string' ? error.config.url : undefined
  const method = typeof error?.config?.method === 'string' ? error.config.method.toUpperCase() : undefined
  const code = typeof data?.code === 'string' ? data.code : undefined

  const message =
    typeof data?.message === 'string'
      ? data.message
      : typeof data?.detail === 'string'
        ? data.detail
        : data && typeof data === 'object'
          ? JSON.stringify(data)
          : typeof error?.message === 'string'
            ? error.message
            : fallback

  let dataPreview = ''
  try {
    const raw = typeof data === 'string' ? data : JSON.stringify(data)
    dataPreview = String(raw || '').slice(0, 300)
  } catch {
    dataPreview = ''
  }

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error('invite_job failed', { status, data, url, method })
  }
  return { message: message || fallback, code, status, url, method, dataPreview }
}

export default function PixeProsSection({
  pros,
  isSelf,
  profileUserId,
  displayName,
  profileHandle,
  onMessage,
}: {
  pros: PixePros
  isSelf: boolean
  profileUserId?: number | null
  displayName?: string | null
  profileHandle?: string | null
  onMessage?: () => void
}) {
  const navigate = useNavigate()

  const [requestOpen, setRequestOpen] = useState(false)
  const [summary, setSummary] = useState('')
  const [locationText, setLocationText] = useState('')
  const [zipcode, setZipcode] = useState('')
  const [urgency, setUrgency] = useState<'ASAP' | 'TODAY' | 'FLEXIBLE'>('ASAP')
  const [sending, setSending] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteTitle, setInviteTitle] = useState(`Invite ${displayName || 'this pro'} to a job`)
  const [inviteSummary, setInviteSummary] = useState('')
  const [inviteCompMode, setInviteCompMode] = useState<'HOURLY' | 'FIXED'>('HOURLY')
  const [inviteFixedPixe, setInviteFixedPixe] = useState('')
  const [inviteHourlyPixe, setInviteHourlyPixe] = useState('')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteErrorDebug, setInviteErrorDebug] = useState<{ status?: number; url?: string; method?: string; dataPreview?: string } | null>(null)

  const bio = (pros.service_description || pros.bio || '').trim()
  const company = pros.company || null
  const skills = normalizeSkills(pros.skills)
  const messageHref = profileHandle ? `/inbox/compose?to=@${encodeURIComponent(profileHandle)}` : '/inbox'
  const cardModel: ProCardViewModel = {
    handle: profileHandle || '',
    display_name: displayName || '',
    title: pros.title || '',
    description: bio,
    hourly_rate_pixe: pros.hourly_rate_cents != null ? Number(pros.hourly_rate_cents) / 100 : null,
    starting_price_pixe: pros.base_rate_cents != null ? Number(pros.base_rate_cents) / 100 : null,
    mode: pros.pro_mode === 'LOCAL' || pros.pro_mode === 'BOTH' ? 'onsite' : 'remote',
    available_now: Boolean(pros.available_now),
    rating_avg: pros.average_rating != null ? Number(pros.average_rating) : null,
    rating_count: pros.rating_count != null ? Number(pros.rating_count) : null,
    jobs_count: pros.completed_jobs_count != null ? Number(pros.completed_jobs_count) : null,
    earned_pixe: pros.total_earned_pixe || 0,
    skills,
    pros_to_go_enabled: Boolean(pros.pros_to_go_enabled),
    company_title: company?.title || '',
    company_slug: company?.slug || '',
    company_role_title: pros.company_role_title || '',
    is_owner: isSelf,
  }

  async function onSubmitRequest() {
    if (!profileUserId) return
    if (!summary.trim() || !locationText.trim() || !zipcode.trim()) {
      setRequestError('Summary, location, and ZIP code are required.')
      return
    }
    setSending(true)
    setRequestError(null)
    try {
      const res = await requestPro(profileUserId, {
        summary: summary.trim(),
        client_location_text: locationText.trim(),
        client_zipcode: zipcode.trim(),
        urgency,
      })
      setRequestOpen(false)
      navigate(`/jobs/${res.job_id}`)
    } catch (e: any) {
      setRequestError(e?.response?.data?.detail || 'Unable to send request.')
    } finally {
      setSending(false)
    }
  }

  async function onSubmitInvite() {
    if (!profileUserId) return
    if (!inviteSummary.trim()) {
      setInviteError('Summary is required.')
      return
    }
    if (!inviteTitle.trim()) {
      setInviteError('Title is required.')
      return
    }

    let fixed: number | null = null
    let hourlyAmount: number | null = null

    if (inviteCompMode === 'FIXED') {
      const v = Number(inviteFixedPixe)
      if (!Number.isFinite(v) || v <= 0) {
        setInviteError('Enter a fixed price in PIXE.')
        return
      }
      fixed = Math.floor(v)
    } else {
      const v = Number(inviteHourlyPixe)
      if (!Number.isFinite(v) || v <= 0) {
        setInviteError('Enter an hourly amount in PIXE.')
        return
      }
      hourlyAmount = Math.floor(v)
    }

    setInviteSending(true)
    setInviteError(null)
    setInviteErrorDebug(null)
    try {
      const res = await inviteProToJob(profileUserId, {
        title: inviteTitle.trim() || undefined,
        summary: inviteSummary.trim(),
        compensation_mode: inviteCompMode,
        proposed_fixed_price_pixe: fixed,
        proposed_hourly_pixe: hourlyAmount,
      })
      setInviteOpen(false)
      navigate(`/jobs/${res.job_id}`)
    } catch (e: any) {
      const parsed = parseApiError(e, 'Unable to send invite.')
      const base = parsed.status ? `Invite failed (HTTP ${parsed.status})` : 'Invite failed (network)'
      const msg = `${base}: ${parsed.message}${parsed.code ? ` (Code: ${parsed.code})` : ''}`
      setInviteError(msg)
      setInviteErrorDebug({
        status: parsed.status,
        url: parsed.url,
        method: parsed.method,
        dataPreview: parsed.dataPreview,
      })
    } finally {
      setInviteSending(false)
    }
  }

  return (
    <>
      <ProPublicCard
        pro={cardModel}
        onEdit={isSelf ? () => navigate('/pros') : undefined}
        right_actions={
          !isSelf && profileUserId ? (
            <>
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="inline-flex rounded-full border border-indigo-300/40 bg-indigo-400/20 px-4 py-2 text-sm font-semibold text-indigo-50 hover:bg-indigo-400/30"
              >
                Invite to job
              </button>
              {onMessage ? (
                <button
                  type="button"
                  onClick={onMessage}
                  className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/15"
                >
                  Message
                </button>
              ) : (
                <Link
                  to={messageHref}
                  className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/15"
                >
                  Message
                </Link>
              )}
              {pros.pros_to_go_enabled ? (
                <button
                  type="button"
                  onClick={() => setRequestOpen(true)}
                  className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/15"
                >
                  Request Pro
                </button>
              ) : null}
            </>
          ) : null
        }
      />

      {requestOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRequestOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-white/10 bg-neutral-950 p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="text-base font-semibold">Request Pro</div>
            <label className="block text-sm">
              Summary
              <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2" />
            </label>
            <label className="block text-sm">
              Location text
              <input value={locationText} onChange={(e) => setLocationText(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2" />
            </label>
            <label className="block text-sm">
              ZIP code
              <input value={zipcode} onChange={(e) => setZipcode(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2" />
            </label>
            <label className="block text-sm">
              Urgency
              <select value={urgency} onChange={(e) => setUrgency(e.target.value as any)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2">
                <option value="ASAP">ASAP</option>
                <option value="TODAY">Today</option>
                <option value="FLEXIBLE">Flexible</option>
              </select>
            </label>
            {requestError ? <div className="text-xs text-red-300">{requestError}</div> : null}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRequestOpen(false)} className="rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm">
                Cancel
              </button>
              <button type="button" onClick={onSubmitRequest} disabled={sending} className="rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {sending ? 'Sending...' : 'Send request'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {inviteOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setInviteOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-white/10 bg-neutral-950 p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="text-base font-semibold">Invite to Job</div>
            <label className="block text-sm">
              Title
              <input value={inviteTitle} onChange={(e) => setInviteTitle(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2" />
            </label>
            <label className="block text-sm">
              Summary
              <textarea value={inviteSummary} onChange={(e) => setInviteSummary(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2" />
            </label>
            <label className="block text-sm">
              Compensation
              <select value={inviteCompMode} onChange={(e) => setInviteCompMode(e.target.value as 'HOURLY' | 'FIXED')} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2">
                <option value="HOURLY">Hourly</option>
                <option value="FIXED">Fixed</option>
              </select>
            </label>
            {inviteCompMode === 'FIXED' ? (
              <label className="block text-sm">
                Fixed price (PIXE)
                <input value={inviteFixedPixe} onChange={(e) => setInviteFixedPixe(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2" />
              </label>
            ) : (
              <label className="block text-sm">
                Hourly (PIXE)
                <input value={inviteHourlyPixe} onChange={(e) => setInviteHourlyPixe(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2" />
              </label>
            )}
            {inviteError ? (
              <div className="space-y-2">
                <div className="text-xs text-red-300">{inviteError}</div>
                {import.meta.env.DEV && inviteErrorDebug ? (
                  <details className="text-[11px] text-white/60">
                    <summary className="cursor-pointer select-none">Details</summary>
                    <div className="mt-1 space-y-1 rounded-md border border-white/10 bg-black/30 p-2">
                      <div>Status: {inviteErrorDebug.status ?? 'network'}</div>
                      <div>Method: {inviteErrorDebug.method || 'unknown'}</div>
                      <div>URL: {inviteErrorDebug.url || 'unknown'}</div>
                      <div>Response: {inviteErrorDebug.dataPreview || 'n/a'}</div>
                    </div>
                  </details>
                ) : null}
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setInviteOpen(false)} className="rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm">
                Cancel
              </button>
              <button type="button" onClick={onSubmitInvite} disabled={inviteSending} className="rounded-lg bg-indigo-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {inviteSending ? 'Sending...' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
